import { Component, input, inject, signal, computed, type Signal, effect, type OnDestroy, untracked } from "@angular/core";
import { FormControl, ReactiveFormsModule } from "@angular/forms";
import { toSignal } from "@angular/core/rxjs-interop";
import { TranslateModule } from "@ngx-translate/core";
import { JumpstartComponentsModule } from "@wk/components-v3-angular17";
import type { PopupUserRequestEvent, PlacementPosition, PlacementOffsets, PlacementWidth } from "@wk/components-v3-angular17";
import { UtilsService, WkTranslatePipe } from "@utils";
import { LanguageService } from "@services";
import { PaginationComponent } from "@components/pagination";
import { FilterService } from "@filter/filter.service";
import { SimpleFilter, DynamicFilter, SimpleFilterMulti } from "@filter/filters";
import { FilterSelectionKind, type FilterOption, type FilterValue, type DateFilterValue, type FilterSelectionList } from "@filter/filter.types";
import { FilterPillsComponent } from "@filter/components/filter-pills";

let nextColumnFilterInstanceId = 0;

@Component({
    selector: "wk-column-filter",
    templateUrl: "./column-filter.component.html",
    styleUrl: "./column-filter.component.scss",
    host: { class: "wk-cf" },
    imports: [TranslateModule, JumpstartComponentsModule, PaginationComponent, WkTranslatePipe, FilterPillsComponent, ReactiveFormsModule],
})
export class ColumnFilterComponent<TItem extends object> implements OnDestroy {
    protected readonly popupTargetId = `popup-column-filter-${nextColumnFilterInstanceId++}`;
    private readonly languageService = inject(LanguageService);
    private readonly filterService = inject(FilterService);
    protected readonly isPopupOpen = computed(() => this.filterService.activeColumnFilterPopupId() === this.popupTargetId);
    protected readonly FilterSelectionKind = FilterSelectionKind;
    protected readonly wkTranslate = inject(WkTranslatePipe);
    protected readonly searchField = new FormControl<string>("");
    protected readonly searchText = toSignal(this.searchField.valueChanges, { initialValue: this.searchField.value });
    protected readonly searchValue = computed(() => UtilsService.foldDiacritics(this.searchText() ?? "", this.languageService.language()));

    constructor() {
        effect(() => {
            this.searchValue();
            untracked(() => this.pageNumber.set(1));
        });
    }

    public label = input<string>();
    public addLabel = input<boolean>(false);
    public filterName = input.required<string>();
    public dynamicFilterName = input<string>();
    public pageSize = input<number>(10);
    public data = input<TItem[] | undefined>(undefined);
    public showAllOptions = input<boolean>(false);

    protected fullFilterName = computed(() => {
        const filterName = this.filterName();
        const dynamicFilterName = this.dynamicFilterName();
        return dynamicFilterName ? `${filterName}_${dynamicFilterName}` : filterName;
    });
    protected filter = computed(() => {
        const filterName = this.filterName();
        const filter = this.filterService.getFilter(filterName);
        if (!filter) return;
        if (filter instanceof SimpleFilter) return filter;

        const dynamicFilterName = this.dynamicFilterName();
        if (!dynamicFilterName) return;
        return (filter as DynamicFilter<FilterSelectionList, TItem>).getFilter(dynamicFilterName);
    });
    protected filterDefinition = computed(() => this.filter()?.definition);
    protected readonly filterOptions = computed<readonly FilterOption[] | undefined>(() => {
        const filterDefinition = this.filterDefinition();
        if (!filterDefinition) return;
        if (filterDefinition.kind !== FilterSelectionKind.Multi) return;
        const filter = this.filter() as SimpleFilterMulti<TItem>;
        const buildOptions = !this.showAllOptions() ? (filter.buildOptions(this.data as Signal<TItem[]>) ?? filter.options) : filter.options;

        return buildOptions() ?? [];
    });
    protected readonly filteredOptions = computed<readonly FilterOption[] | undefined>(() => {
        const searchText = this.searchValue();
        const options = this.filterOptions();
        if (!options) return [];
        if (!searchText) return options;
        return options.filter((option) => UtilsService.foldDiacritics(this.wkTranslate.transform(option.label)).includes(searchText.toLowerCase()));
    });
    protected filterSelection = computed(() => {
        const selection = this.filter()?.selection();
        if (!selection) return;
        switch (selection.kind) {
            case FilterSelectionKind.Text:
                if (!("value" in selection) || !selection.value) return;
                return selection.value as string;
            case FilterSelectionKind.Date:
                if (!("value" in selection) || !selection.value) return;
                return selection.value as DateFilterValue;
            case FilterSelectionKind.Multi:
                if (!("values" in selection) || !selection.values || !selection.values.length) return;
                return (selection.values ?? []) as readonly FilterValue[];
            default:
                return;
        }
    });
    protected filterSelectionLength = computed<number>(() => {
        const definition = this.filterDefinition();
        if (!definition) return 0;
        if (definition.kind !== FilterSelectionKind.Multi) return 0;
        const selection = (this.filterSelection() as readonly FilterValue[]) ?? [];
        return selection.length;
    });
    protected isMultiSelection = computed(() => this.filterDefinition()?.kind === FilterSelectionKind.Multi);
    protected isDateSelection = computed(() => this.filterDefinition()?.kind === FilterSelectionKind.Date);

    // FilterSelectionKind is Multi
    protected pagedOptions = computed<FilterOption<FilterValue>[] | undefined>(() => {
        const filterDefinition = this.filterDefinition();
        if (!filterDefinition) return;
        if (filterDefinition.kind !== FilterSelectionKind.Multi) return;
        const options = this.filteredOptions();
        if (!options) return [];
        return options.slice(this.startIndex(), this.pageNumber() * this.pageSize());
    });
    protected startIndex = computed<number>(() => (this.pageNumber() - 1) * this.pageSize());
    protected pageNumber = signal<number>(1);
    protected totalItemsCount = computed<number>(() => this.filteredOptions()?.length || 0);
    protected maxPageSize = computed<number>(() => Math.ceil(this.totalItemsCount() / this.pageSize()));

    protected onPopupRequest(e: PopupUserRequestEvent): void {
        if (e.source !== "targetClick") {
            if (this.isPopupOpen()) this.filterService.activeColumnFilterPopupId.set(null);
            return;
        }

        if (this.isPopupOpen()) {
            this.filterService.activeColumnFilterPopupId.set(null);
            return;
        }

        const definition = this.filterDefinition();
        if (!definition) return;
        if (definition.kind === FilterSelectionKind.Multi) this.searchField.setValue("");
        this.filterService.activeColumnFilterPopupId.set(this.popupTargetId);
    }

    ngOnDestroy(): void {
        if (this.isPopupOpen()) this.filterService.activeColumnFilterPopupId.set(null);
    }

    protected onFilterChange(option: FilterOption): void {
        const filter = this.filter();
        if (!filter) return;
        filter.addToSelection(option.value);
    }

    protected onClearAll(): void {
        const filter = this.filter();
        if (!filter) return;
        filter.clear();
    }

    protected onPageChange(page: number): void {
        this.pageNumber.set(page);
    }

    protected onSelectAll(): void {
        const filter = this.filter();
        if (!filter) return;
        const options = this.filterOptions();
        const selected = options?.map((option) => option.value) ?? null;
        filter.setSelection(selected);
    }

    protected isOptionSelected(option: FilterOption): boolean {
        const selection = this.filterSelection();
        if (Array.isArray(selection)) {
            return selection.includes(option.value);
        }
        return false;
    }

    protected popupPlacement: Partial<{
        positions: PlacementPosition[];
        offsets: PlacementOffsets;
        width: PlacementWidth;
    }> = {
        positions: ["bottom"],
        width: "auto",
        offsets: {
            bottom: {
                leftOffset: 0,
                topOffset: 30,
            },
        },
    };
}
